import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AdminRolesResponse,
  AdminUser,
  AdminUsersResponse,
  CreateUserPayload,
  McpAccessTier,
  UpdateUserPayload,
} from './admin-users.types';

/** Admin staff-administration API: list/create users, edit authority, manage roles. */
@Injectable({ providedIn: 'root' })
export class AdminUsersService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/admin`;

  /** Every user, optionally filtered by a name/email `search` term. */
  list(search = ''): Observable<AdminUsersResponse> {
    const params = search ? { search } : undefined;
    return this.http.get<AdminUsersResponse>(`${this.base}/users/`, { params });
  }

  /** Create a staff user; they are emailed a set-password invite. */
  create(payload: CreateUserPayload): Observable<AdminUser> {
    return this.http.post<AdminUser>(`${this.base}/users/`, payload);
  }

  /** Patch a user's ops tier / Credit-Committee flag / active state. */
  update(id: string, payload: UpdateUserPayload): Observable<AdminUser> {
    return this.http.patch<AdminUser>(`${this.base}/users/${id}/`, payload);
  }

  /** Assign a SYSTEM role to a user (idempotent). */
  assignRole(id: string, roleSlug: string): Observable<AdminUser> {
    return this.http.post<AdminUser>(`${this.base}/users/${id}/roles/`, {
      role_slug: roleSlug,
    });
  }

  /** Revoke a SYSTEM role from a user (soft). */
  revokeRole(id: string, roleSlug: string): Observable<AdminUser> {
    return this.http.request<AdminUser>(
      'delete',
      `${this.base}/users/${id}/roles/`,
      { body: { role_slug: roleSlug } },
    );
  }

  /** Grant or change a user's MCP access tier. */
  setMcpAccess(id: string, tier: McpAccessTier): Observable<AdminUser> {
    return this.http.put<AdminUser>(`${this.base}/users/${id}/mcp-access/`, {
      tier,
    });
  }

  /** Revoke a user's MCP access (tier back to none). */
  revokeMcpAccess(id: string): Observable<AdminUser> {
    return this.http.delete<AdminUser>(`${this.base}/users/${id}/mcp-access/`);
  }

  /** Available SYSTEM roles for the picker. */
  roles(): Observable<AdminRolesResponse> {
    return this.http.get<AdminRolesResponse>(`${this.base}/roles/`);
  }
}
